<template>
  <div class="main-body">
    <div class="title-bar">
      <h1>My Profile</h1>
    </div>
    <div class="user-profile">
      <img src="../assets/user-profile-large.svg" />
      <div class="general-info">
        <h3>General Information</h3>
        <div class="inputs">
          <TextBox label="First Name" v-model="userToEdit.firstName" :required="true" :invalid="showErrors && !userToEdit.firstName" />
          <TextBox label="Last Name" v-model="userToEdit.lastName" :required="true" :invalid="showErrors && !userToEdit.lastName" />
          <TextBox label="Email" v-model="userToEdit.email" :readonly="true" />
          <Dropdown label="Start Page" :options="startPages" class="dropdown-input" v-model="userToEdit.startPage" />
          <Dropdown
            label="Out of Office"
            :options="[
              { text: 'Yes', value: true },
              { text: 'No', value: false },
            ]"
            class="dropdown-input"
            @change="setBackInOfficeDate"
            v-model="userToEdit.outOfOffice" />
          <TextBox label="Turn Off Out of Office" type="date" v-model="userToEdit.backInOffice" :disabled="!userToEdit.outOfOffice" />
        </div>
        <div class="actions">
          <button @click="close()" class="text-btn">close</button>
          <button @click="showResetPasswordConfirmation = true" class="text-btn blue">reset Password</button>
          <button @click="showResetMFAConfirmation = true" class="text-btn blue">reset MFA</button>
          <button :disabled="!isDirty || isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="save()">Save<img src="../assets/spinner.svg" /></button>
        </div>
      </div>
    </div>

    <!-- Reset Password Confirmation -->
    <Confirmation
      v-if="showResetPasswordConfirmation"
      header="Reset Password"
      message="Are you sure you want to reset your password?"
      confirmText="Reset Password"
      cancelText="Cancel"
      :serious="false"
      @confirm="confirmResetPassword"
      @cancel="showResetPasswordConfirmation = false" />

    <!-- Reset MFA Confirmation -->
    <Confirmation
      v-if="showResetMFAConfirmation"
      header="Reset Multi-Factor Authentication"
      message="Are you sure you want to reset your MFA settings? You will need to set up multi-factor authentication again on your mobile phone."
      confirmText="Reset MFA"
      cancelText="Cancel"
      :serious="true"
      @confirm="confirmResetMFA"
      @cancel="showResetMFAConfirmation = false" />
  </div>
</template>

<script>
import useCurrentUser from '@/composables/useCurrentUser';
import useUserProfileMutate from '@/composables/useUserProfileMutate';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useAlerts from '@/composables/useAlerts';
import useSaveProtect from '../composables/useSaveProtect';
import Dropdown from '@/components/inputs/Dropdown.vue';
import TextBox from '@/components/inputs/TextBox.vue';
import Confirmation from '@/components/Confirmation.vue';
import { resetUserMFA, resetPW } from '@/composables/useAuthMutate';

export default {
  name: 'UserProfile',
  components: {
    Dropdown,
    TextBox,
    Confirmation,
  },
  setup() {
    const { decryptSymbols, encryptSymbols } = useDecryptSymbols();
    const { user, getCurrentUser } = useCurrentUser();
    const { updateUserProfile } = useUserProfileMutate();
    const { addAlert } = useAlerts();
    const { state: saveProtectState } = useSaveProtect();

    return { user, updateUserProfile, decryptSymbols, encryptSymbols, addAlert, saveProtectState, getCurrentUser, resetUserMFA, resetPW };
  },
  data() {
    return {
      isDirty: false,
      isSaving: false,
      showErrors: false,
      userToEdit: {},
      originalUser: {},
      showResetPasswordConfirmation: false,
      showResetMFAConfirmation: false,
    };
  },
  computed: {
    startPages() {
      const startPage = [];
      if (this.user.showLeads) {
        startPage.push({ text: 'Leads', value: 'leads' });
      }
      if (this.user.showQuotes) {
        startPage.push({ text: 'Quotes', value: 'quotes' });
      }
      if (this.user.showContacts) {
        startPage.push({ text: 'Contacts', value: 'contacts' });
      }
      if (this.user.showImpactReport) {
        startPage.push({ text: 'Sales Impact Report', value: 'salesImpact' });
      }
      return startPage;
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.updateUser();
      },
    },
    userToEdit: {
      deep: true,
      handler() {
        if (JSON.stringify(this.userToEdit) === JSON.stringify(this.originalUser)) {
          this.isDirty = false;
        } else {
          this.isDirty = true;
        }
      },
    },
    isDirty(val) {
      this.saveProtectState.isDirty = val;
    },
  },
  methods: {
    async save() {
      if (!this.verifyFields()) return;
      this.isSaving = true;
      this.isDirty = false;
      const userToSave = {
        USERID: this.userToEdit.userid,
        FIRSTNAME: this.userToEdit.firstName,
        LASTNAME: this.userToEdit.lastName,
        STARTPAGE: this.userToEdit.startPage,
        OOO: this.userToEdit.outOfOffice,
        BACKINOFFICE: this.userToEdit.backInOffice,
      };
      const encryptedUser = this.encryptSymbols(userToSave);

      const returnedData = await this.updateUserProfile(encryptedUser);
      this.isSaving = false;

      if (!returnedData) {
        this.isDirty = true;
        return;
      }

      this.originalUser = { ...this.userToEdit };
      this.addAlert('Your profile has been updated', 'low', 3);
      this.getCurrentUser();
    },
    verifyFields() {
      if (!this.userToEdit.displayName) {
        this.addAlert('Please fill out all required fields', 'high', 4);
        this.showErrors = true;
        return false;
      }
      this.showErrors = false;
      return true;
    },
    close() {
      window.history.back();
    },
    updateUser() {
      this.userToEdit = { ...this.user };
      this.userToEdit.backInOffice = this.userToEdit.backInOffice ? this.getISODate(new Date(parseInt(this.userToEdit.backInOffice))) : null;
      this.originalUser = { ...this.userToEdit };
    },
    setBackInOfficeDate() {
      if (!this.userToEdit.outOfOffice) {
        this.userToEdit.backInOffice = null;
      } else {
        this.userToEdit.backInOffice = this.getISODate(new Date());
      }
    },
    getISODate(date) {
      return date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}` : null;
    },
    confirmResetPassword() {
      this.showResetPasswordConfirmation = false;
      this.resetPW();
    },
    confirmResetMFA() {
      this.showResetMFAConfirmation = false;
      this.resetUserMFA();
    },
  },
  mounted() {
    this.getCurrentUser();
    this.updateUser();
  },
};
</script>

<style lang="scss" scoped>
.user-profile {
  flex: 1;
  background: white;
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  img {
    width: 13.5rem;
    padding: 2rem 6rem;
  }

  .general-info {
    flex: 1;
    padding: 1.5rem;

    .inputs {
      display: flex;
      flex-direction: row;
      margin-top: 1.5rem;
      flex-wrap: wrap;
      margin-right: 2rem;
      gap: 1.5rem 1rem;

      .primary-input,
      .dropdown-input {
        min-width: 20rem;
        flex: 1 0 30%;
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-top: 2rem;
      margin-right: 2rem;
    }
  }
}
</style>
